import React, { useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import Image from 'next/image'

import { getCookie } from 'cookies-next'
import styled from '@emotion/styled'
import { SwiperSlide } from 'swiper/react'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import ReactHtmlParser from 'react-html-parser'

import { getFrontStoreId, postKtcPoiByid3, getFrontProduct } from 'api'
import {
  factorizeOption,
  promotionBanner,
  changeDay,
  isEmptyObj,
  setRwgLocalstorage,
} from 'lib'
import Header from 'components/Header'
import Carousel from 'components/common/Carousel'
import ProductCard from 'components/StoreSection/ProductCard'
import ProductCardVertical from 'components/StoreSection/product/ProductCard'
import { BLUR, HOST, TAIL } from 'constants/image'
import { DEFAULT_LANG } from 'constants/lang'
import Custom404 from 'pages/404'
import Space from 'components/common/Space'
import KTCFooter from 'components/ktc/KTCFooter'
import KTCPromotions from 'components/ktc/KTCPromotions'
import KTCHorizen from 'components/ktc/KTCHorizen'
import MemberJoin from 'components/ktc/MemberJoin'
import ModalStore from 'components/StoreSection/ModalStore'
import RWGFooter from 'components/StoreSection/RWGFooter'
import { useRouter } from 'next/router'

const Store = ({ subdomain, initialData, initialProduct, initialKtcData }) => {
  if (!initialData) return <Custom404 />
  if (
    initialData.data?.category !== 'food' ||
    initialData.rwg?.category === 'appointments'
  )
    return <Custom404 />

  const {
    t,
    i18n: { language: lang },
  } = useTranslation()
  const [storeData, setStoreData] = useState(initialData)
  const [didMount, setDidMount] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [liveEventCount, setLiveEventCount] = useState(
    initialKtcData.live_event
  )

  const {
    data,
    products,
    options,
    promotions,
    contract,
    store_files,
    category_img,
  } = storeData

  const { products: allProducts } = initialData

  useEffect(() => {
    if (!didMount) return
    callGetFrontStoreId()

    localStorage.setItem('rwg_store_name', data.translate_name ?? data.name)
  }, [lang])

  useEffect(() => {
    if (subdomain === 'reserve') {
      setRwgLocalstorage(
        data.translate_name ?? data.name,
        initialData.rwg_rending_type
      )
    }
  }, [])

  useEffect(() => {
    setDidMount(true)
  }, [])

  // API 호출 함수
  const callGetFrontStoreId = async () => {
    await getFrontStoreId({
      storeId: data.id,
      s_lang: lang,
      offset: 1,
    }).then((res) => setStoreData(res))
  }

  //  렌더링 컨텐츠 편집
  const sortFacilities = () => {
    let arr = []
    const selected = factorizeOption(data.option)
    Object.values(options).map((item) =>
      arr.push({
        name: item.name[lang],
        value: item.value,
        selected: selected.includes(item.value),
      })
    )
    return arr
  }

  const ktcIcons = () => {
    if (initialKtcData.error) return

    let arr = []
    initialKtcData.icon?.map((item) => arr.push(Object.values(item)[0]))
    return arr
  }

  const countStoreImage = useCallback(() => {
    let count = 0
    store_files.map(
      (file) => file.type === 'represent' && file.is_main === 0 && count++
    )
    return count
  }, [])

  const collapseHandler = useCallback((e) => {
    const button = e.currentTarget
    const target = button.previousElementSibling

    if (target.style.height !== 'auto') {
      target.style.height = 'auto'
      button.innerText = 'See Less'
    } else {
      target.style.height = '320px'
      button.innerText = 'See More'
    }
  }, [])

  const signitureMenu = (
    <Carousel
      contents={products.map((item) => (
        <SwiperSlide key={item.id} style={{ width: 180 }}>
          <ProductCard
            id={item.id}
            store_id={item.store_id}
            image={
              item.is_reserve === 1
                ? category_img.length > 0 && category_img[0].image_path
                : item.product_image
            }
            name={item.translate_name}
            price={item.price}
            currency={item.currency}
            promotions_2={initialKtcData.promotions_2}
          />
        </SwiperSlide>
      ))}
      option={{
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
        spaceBetween: 16,
      }}
    />
  )

  const signitureMenuRwg = (
    <section>
      <div
        style={{
          height: 320,
          overflowY: 'hidden',
        }}
      >
        <Space bottom={32} />
        <ul>
          {allProducts.map(
            (item) =>
              item.is_main === 1 && (
                <ProductCardVertical
                  key={item.id}
                  id={item.id}
                  store_id={item.store_id}
                  label={item.translate_label_name}
                  image={item.product_image}
                  name={item.translate_name ?? item.name}
                  price={item.price}
                  currency={item.currency}
                  promotions_2={initialKtcData.promotions_2}
                  catgory="food"
                />
              )
          )}
        </ul>

        <Space top={64} />
        <h4>{t('product:normal_store')}</h4>
        <Space bottom={32} />
        {allProducts.map((item) => (
          <ProductCardVertical
            key={item.id}
            id={item.id}
            store_id={item.store_id}
            image={item.product_image}
            name={item.translate_name ?? item.name}
            price={item.price}
            currency={item.currency}
            promotions_2={initialKtcData.promotions_2}
            catgory="food"
          />
        ))}
      </div>

      <SeeMore onClick={(e) => collapseHandler(e)}>See More</SeeMore>
    </section>
  )

  return (
    <>
      <Header
        title={data.translate_name}
        style={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}
      />

      {/* 상점 이미지 */}
      <Carousel
        option={{
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
          pagination:
            countStoreImage() < 10 ? { clickable: true } : { type: 'fraction' },
          navigation: countStoreImage() > 10 ? true : false,
        }}
        contents={
          <>
            {data.store_image && (
              <SwiperSlide>
                <Image
                  src={`${HOST}${data.store_image}${TAIL}`}
                  priority
                  alt=""
                  width={0}
                  height={0}
                  sizes="100%"
                  style={{
                    width: '100%',
                    height: 300,
                    objectFit: 'cover',
                    padding: '0 16px',
                  }}
                />
              </SwiperSlide>
            )}

            {store_files.length > 0 &&
              store_files.map(
                (item, index) =>
                  item.type === 'represent' &&
                  item.is_main === 0 && (
                    <SwiperSlide key={item.id}>
                      <Image
                        src={item.path ? `${HOST}${item.path}${TAIL}` : BLUR}
                        alt=""
                        priority={index === 0}
                        width={0}
                        height={0}
                        sizes="100%"
                        style={{
                          width: '100%',
                          height: 300,
                          objectFit: 'cover',
                          padding: '0 16px',
                        }}
                      />
                    </SwiperSlide>
                  )
              )}
          </>
        }
      ></Carousel>

      <Container>
        {/* KTC 상점정보 */}
        <KtcStoreInfo>
          {/* 카테고리 / 상점이름 / 이벤트 안내 모달 */}
          <div
            className="display-flex clamp2 store-name"
            style={{ justifyContent: 'space-between' }}
          >
            <div className="display-flex">
              <Image
                src={
                  category_img.length > 0
                    ? `${HOST}${category_img[0].image_path}${TAIL}`
                    : '/ktc/food.png'
                }
                alt=""
                width={24}
                height={24}
                style={{ marginRight: 8 }}
              />
              {initialKtcData.name ?? data.translate_name}
            </div>
            {showModal && (
              <ModalStore
                data={initialKtcData}
                onClose={setShowModal}
                showIcons
              />
            )}
            {!initialKtcData.error && (
              <Image
                src="/ktc/info.png"
                alt=""
                width={24}
                height={24}
                style={{ marginRight: 8, cursor: 'pointer' }}
                onClick={() => setShowModal(true)}
              />
            )}
          </div>

          <Space bottom={8} />

          <p id="address">
            {data.translate_address_road}{' '}
            {data.translate_address_etc === '' || !data.translate_address_etc
              ? null
              : data.translate_address_etc}
          </p>

          {/* 머스트잇 */}
          <p className="label">
            {initialKtcData.label}
            {initialKtcData.must_eat && ' | '}
            {initialKtcData.must_eat}
          </p>
          <Space bottom={16} />

          {/* KTC 프로모션 혜택 */}
          {!initialKtcData.error && (
            <KTCPromotions
              promotions_2={initialKtcData.promotions_2}
              rate
              highlight
              styles={{
                backgroundColor: '#f9f9f9',
                borderRadius: 8,
                marginLeft: 10,
              }}
            />
          )}
          <Space bottom={16} />

          {/* 상점 설명 */}
          <Description>
            {ReactHtmlParser(data.translate_description) ??
              initialKtcData.introduction}
          </Description>
          <Space bottom={8} />

          {/* 어워드 아이콘 */}
          {!initialKtcData.error && (
            <div style={{ margin: '16px 0' }}>
              {ktcIcons().map(
                (item, index) =>
                  item && (
                    <Image
                      key={index}
                      src={`/ktc/store-info${index + 1}.png`}
                      alt=""
                      width={24}
                      height={24}
                      style={{ marginRight: 4 }}
                    />
                  )
              )}
            </div>
          )}

          {/* 카드 받으러 가기 */}
          <MemberJoin />
        </KtcStoreInfo>

        {/* 매장주소 / 지도 */}
        <Etc>
          <KTCHorizen />

          <h4>{t('ktc:location_information')}</h4>
          <Link
            key={data.id}
            href={`https://www.google.com/maps/search/?api=1&query=${data.latitude},${data.longitude}`}
            target="_blank"
          >
            <Image
              src={`https://maps.googleapis.com/maps/api/staticmap?language=ko&center=${data.latitude},${data.longitude}&zoom=18&size=1000x250&maptype=roadmap&markers=color:red%7C${data.latitude},${data.longitude}&key=${process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY}`}
              alt=""
              width={0}
              height={0}
              sizes="100%"
              style={{
                width: '100%',
                height: 180,
                border: '1px solid rgba(0,0,0,0.1)',
                marginTop: 10,
              }}
            />
          </Link>
          <Clipboard
            onClick={() =>
              window.navigator.clipboard.writeText(
                document.querySelector('#address').innerHTML
              )
            }
          >
            {t('ktc:copy_address')}
          </Clipboard>

          <p id="address">
            {data.translate_address_road}{' '}
            {data.translate_address_etc === '' || !data.translate_address_etc
              ? null
              : data.translate_address_etc}
          </p>

          <FindRoute
            onClick={() =>
              window.open(
                `https://www.google.com/maps/dir/?api=1&origin=${encodeURI(
                  '내 위치'
                )}&destination=${initialKtcData.lat},${
                  initialKtcData.lng
                }&travelmode=transit`
              )
            }
          >
            {t('ktc:finding_direction')}
          </FindRoute>

          <KTCHorizen />
        </Etc>

        {/* 프로모션 */}
        {subdomain !== 'reserve' && (
          <section>
            {/* 배너 중 KTC앱 채널 관련 */}
            {initialData.store_banner.length > 0 &&
              initialData.store_banner.map((item, index) => (
                <Link key={item.id} href={`/theme/${item.last_part}`}>
                  {index === 0 && <Space top={16} />}
                  <Image
                    src={`${HOST}${item.path}${TAIL}`}
                    alt=""
                    sizes="100%"
                    width={0}
                    height={0}
                    style={{
                      width: '100%',
                      height: '100%',
                      paddingTop: 10,
                      marginTop: 10,
                      borderTop: '1px solid #d9d9d9',
                    }}
                  />
                </Link>
              ))}

            {/* 레드테이블 프로모션 */}
            {promotions.length > 0 &&
              promotions.map((item) => (
                <Promotion key={item.promotion_id}>
                  <Image
                    src="/ktc/coupon.png"
                    alt=""
                    width={24}
                    height={24}
                    style={{ marginRight: 8, marginTop: 2 }}
                  />
                  <div>
                    <p className="promotion-name">
                      {!item.translate_name || item.translate_name === ''
                        ? item.name
                        : item.translate_name}
                    </p>
                    {promotionBanner(item, lang)}
                  </div>
                </Promotion>
              ))}

            <KTCHorizen />
          </section>
        )}

        {/* 매장정보, 시설정보 */}
        <section>
          <div
            style={{
              height: 320,
              overflowY: 'hidden',
            }}
          >
            <Etc>
              <h4>{t('ktc:restaurant_information')}</h4>
              <p>{t('ktc:phone_number')}</p>
              <p>{data.telephone}</p>
              <Space bottom={16} />

              {!isEmptyObj(initialKtcData.hours) && (
                <p>{t('ktc:business_hours')}</p>
              )}
              <ul>
                {!initialKtcData.error &&
                  !isEmptyObj(initialKtcData.hours) &&
                  initialKtcData.hours.days.map((item, index) => (
                    <li
                      key={index}
                      style={{
                        width: 220,
                        color: '#777',
                        fontWeight: index === 0 ? 700 : 300,
                      }}
                    >
                      <div className="business-hours-li-top">
                        <div>{changeDay(lang, item.day)}</div>
                        <div>{item.optime}</div>
                        {item.status === 0 && (
                          <div className="closed">{t('ktc:closed')}</div>
                        )}
                      </div>
                      {item.break && (
                        <div className="business-hours-li-bottom">
                          <div>{t('ktc:break')}</div>
                          <div>{item.break}</div>
                        </div>
                      )}
                      {item.last_order && (
                        <div className="business-hours-li-bottom">
                          <div>{t('ktc:last_order')}</div>
                          <div>{item.last_order}</div>
                        </div>
                      )}
                    </li>
                  ))}
              </ul>

              <KTCHorizen />

              <h4>{t('store:facility_info')}</h4>
              <ul>
                {sortFacilities().map(
                  (item) =>
                    item.selected && (
                      <Facility key={item.value}>{item.name}</Facility>
                    )
                )}
              </ul>
            </Etc>
          </div>
          <SeeMore onClick={(e) => collapseHandler(e)}>See More</SeeMore>
        </section>

        {/* 대표메뉴 */}
        {products.length > 0 && (
          <>
            <KTCHorizen />

            <Menu>
              <h4>
                {t('store:signature_menu')}
                <span style={{ float: 'right' }}>
                  <Link href={`/food/${data.id}/product`}>{t('more')}</Link>
                </span>
              </h4>
              {/* {subdomain !== 'reserve' ? signitureMenu : signitureMenuRwg} */}
              {subdomain !== 'reserve' ? signitureMenu : signitureMenuRwg}
            </Menu>
          </>
        )}
      </Container>

      {subdomain === 'reserve' &&
      initialData.rwg_rending_type &&
      initialData.rwg_rending_type !== 'paid' ? (
        <RWGFooter store_id={data.id} />
      ) : (
        <KTCFooter
          id={data.id}
          live_event={liveEventCount}
          onCount={setLiveEventCount}
          status={data.status === 'normal' && contract.status === 'normal'}
          message={t('ktc:pay&reserve')}
          mode="store"
        />
      )}
    </>
  )
}

export default Store

export const getServerSideProps = async (ctx) => {
  const { storeId } = ctx.query

  const subdomain = getCookie('subdomain', ctx)

  const initialLang =
    ctx.locale === 'zh-cn' ? 'zh-Hans' : getCookie('lang', ctx) || DEFAULT_LANG

  const initialData = await getFrontStoreId(
    {
      storeId: parseInt(storeId),
      s_lang: initialLang,
    },
    'server'
  )
  const initialProduct = await getFrontProduct(
    {
      s_lang: initialLang,
      s_store_id: parseInt(storeId),
      limit: 300,
    },
    'server'
  )

  const initialKtcData = await postKtcPoiByid3(
    {
      lang: initialLang,
      store_id: storeId,
    },
    'server'
  )

  const redirectCondition = [
    subdomain === 'reserve' && initialData.rwg?.category === 'appointment',
    subdomain === 'reserve' &&
      initialData.rwg?.category === 'food' &&
      initialData.rwg?.status === 'open' &&
      initialData.rwg_rending_type &&
      initialData.rwg_rending_type !== 'paid',
  ]

  if (redirectCondition.includes(true)) {
    return {
      redirect: {
        destination: `/appointment/${initialData.data.id}`,
        permanent: true,
      },
    }
  } else {
    return {
      props: {
        subdomain,
        initialData: initialData.data === null ? false : initialData,
        initialProduct,
        initialKtcData,
        ...(await serverSideTranslations(ctx.locale, [
          'common',
          'store',
          'ktc',
          'product',
        ])),
      },
    }
  }
}

const KtcStoreInfo = styled.section`
  padding-top: 16px;

  .benefit-ul {
    background-color: #f9f9f9;
    border-radius: 8px;
    padding: 10px;
    margin-left: 10px;
  }

  .store-name {
    font-size: 20px;
  }

  .display-flex {
    display: flex;
    align-items: center;
  }

  .label {
    color: #666;
  }

  .promotions_rate {
    font-size: 14px;
    font-weight: 700;
  }

  .vertical-line {
    padding-left: 4px;
    margin-right: 4px;
  }

  .benefits:last-child .vertical-line {
    display: none;
  }
`

const Container = styled.main`
  padding: 0 16px 150px 16px;
  color: #333;
`

const Promotion = styled.div`
  border-top: 1px solid #d9d9d9;
  padding: 15px 25px 15px 25px;
  margin-top: 10px;
  display: flex;

  .promotion-name {
    font-size: 18px;
    font-weight: 800;
  }

  .discount {
    font-size: 18px;
    color: red;
    font-weight: 900;
  }

  p {
    font-size: 14px;
  }
`

const Menu = styled.section`
  margin-top: 16px;

  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #121212;
    margin-bottom: 5px;
  }

  span {
    font-size: 12px;
    font-weight: 300;
    color: #777;
    cursor: pointer;
  }
`

const Description = styled.p`
  white-space: pre-line;
  font-size: 14px;
  font-weight: 300;
  color: #777;
`
const Etc = styled.section`
  margin-top: 16px;

  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #121212;
    margin-bottom: 5px;
  }

  p {
    color: #777;
  }

  td {
    color: #777;
  }

  .font-weight-400 {
    font-weight: 400;
  }

  .business-hours-li-top,
  .business-hours-li-bottom {
    display: flex;
    justify-content: space-between;
  }

  .business-hours-li-top {
    margin-top: 10px;
  }

  .closed {
    padding: 4px 8px;
    color: #e7211b;
    font-size: 12px;
    border-radius: 16px;
    border: 1px solid #e7211b;
    margin-left: 8px;
  }
`

const Clipboard = styled.span`
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  margin: 8px 0;
  display: inline-block;
`

const FindRoute = styled.button`
  width: 100%;
  height: 40px;
  border-radius: 8px;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  color: #333;
  border: 1px solid #333;
  cursor: pointer;
`

const Facility = styled.li`
  font-size: 15px;
  font-weight: 300;
  width: fit-content;
  display: inline-block;
  margin-bottom: 8px;
  color: #333;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #333;
  margin-right: 8px;
`

const SeeMore = styled.button`
  background-color: white;
  border: 1px solid black;
  border-radius: 8px;
  width: 150px;
  height: 30px;
  margin: 16px auto 0;
  display: block;
  cursor: pointer;
`
